import { PropTypes } from "prop-types";
import React, { useState } from "react";

import { Button } from "@/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast.js";
import "@xyflow/react/dist/style.css";
import { Plus } from "lucide-react";
import { useCreateProject } from "../hooks/projects";
import * as projectsService from "../service/projects";
import useProjectStore from "../store/projects";
import ApiKeyDisplay from "./ApiKeyDisplay";
import CreateProjectForm from "./CreateProjectForm";

const CreateProjectDialog = ({ onClose = () => {} }) => {
  const { modals, closeModal, setModalOpen, setCurrentProject } =
    useProjectStore();
  const [step, setStep] = useState(1);
  const [apiKey, setApiKey] = useState("");
  const { toast } = useToast();
  const { createProject } = useCreateProject();

  const handleCreateProject = async (data) => {
    try {
      const project = await createProject(data);
      setCurrentProject({ id: project.id });
      // Create API key for the new project
      const keyResponse = await projectsService.createApiKey(project.id);
      setApiKey(keyResponse.key);
      setStep(2);
    } catch (err) {
      toast({
        title: "Error!",
        description: "There was an error creating the project. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleApiKeyDone = () => {
    setApiKey("");
    setStep(1);
    closeModal("create", false);
    onClose();
  };

  return (
    <Dialog
      open={modals.create}
      onOpenChange={(value) => setModalOpen("create", value)}
    >
      <DialogTrigger asChild>
        <Button
          variant="primary"
          size="compact"
          className="text-md font-semibold"
          aria-label="Add Project"
        >
          <Plus className="size-10" />
          <span>Add Project</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle>Add New Project</DialogTitle>
            </DialogHeader>
            <CreateProjectForm
              onCancel={() => {
                closeModal("create");
                onClose();
              }}
              onSubmit={handleCreateProject}
            />
          </>
        )}
        {step === 2 && (
          <>
            <DialogHeader>
              <DialogTitle>Save your Key</DialogTitle>
            </DialogHeader>
            <ApiKeyDisplay apiKey={apiKey} onDone={handleApiKeyDone} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

CreateProjectDialog.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

export default CreateProjectDialog;
